<ph-card flat *ngIf="!bare; else bareCard" class="question-card" tabindex="0" role="group" [attr.aria-labelledby]="cardHeadingId">
    <ng-container *ngTemplateOutlet="cardBody"></ng-container>
</ph-card>
<ng-template #bareCard>
    <div class="question-card" tabindex="0" role="group" [attr.aria-labelledby]="cardHeadingId">
        <ng-container *ngTemplateOutlet="cardBody"></ng-container>
    </div>
</ng-template>

<ng-template #cardBody>
    <ph-stack>
        <ph-toolbar justify="between" nowrap *ngIf="batch?.leadIn || totalQuestions > 1">
            <div [id]="cardHeadingId">{{ batch?.leadIn }}</div>
            <ph-badge *ngIf="totalQuestions > 1" variant="light" color="gray" size="small">{{ displayIndex }} of {{ totalQuestions }}</ph-badge>
        </ph-toolbar>

        <ng-container *ngIf="currentQuestion as q">
            <ph-stack gap="tight">
                <strong data-test="question-prompt">{{ q.prompt }}</strong>
                <ph-hint *ngIf="!q.required" data-test="question-optional">Optional</ph-hint>
                <ph-hint *ngIf="q.context">{{ q.context }}</ph-hint>
                <ph-hint *ngIf="q.help" data-test="question-help">{{ q.help }}</ph-hint>
            </ph-stack>

            <div [ngSwitch]="effectiveKind(q)">
                <ph-stack gap="snug" *ngSwitchCase="'single_select'">
                    <ph-list-row *ngFor="let opt of optionsFor(q); let i = index; trackBy: trackByOptionValue"
                                 class="option-row"
                                 variant="card"
                                 wrap
                                 [heading]="opt.label"
                                 [meta]="opt.hint || ''"
                                 [selected]="isOptionSelected(q, opt)"
                                 [open]="isKeyboardFocused(i) && !isOptionSelected(q, opt)"
                                 (ph-select)="onSingleSelectClick(q, opt); onOptionPicked()"
                                 (mouseenter)="onRowHover(i)">
                        <ph-badge slot="actions" *ngIf="opt.recommended" variant="light" color="blue" size="small">Recommended</ph-badge>
                        <ph-kbd slot="actions" *ngIf="i < 9">{{ i + 1 }}</ph-kbd>
                    </ph-list-row>
                    <ng-container *ngIf="q.allow_other">
                        <ng-container *ngTemplateOutlet="otherRow; context: { q: q }"></ng-container>
                    </ng-container>
                </ph-stack>

                <ph-stack gap="snug" *ngSwitchCase="'multi_select'">
                    <ph-list-row *ngFor="let opt of optionsFor(q); let i = index; trackBy: trackByOptionValue"
                                 class="option-row multi"
                                 variant="card"
                                 wrap
                                 [heading]="opt.label"
                                 [meta]="opt.hint || ''"
                                 [open]="isKeyboardFocused(i)"
                                 (ph-select)="onMultiSelectToggle(q, opt)"
                                 (mouseenter)="onRowHover(i)">
                        <ph-badge slot="actions" *ngIf="opt.recommended" variant="light" color="blue" size="small">Recommended</ph-badge>
                        <ph-checkbox slot="actions" [checked]="isOptionSelected(q, opt)" [attr.aria-label]="opt.label" (ph-change)="onMultiSelectToggle(q, opt)"></ph-checkbox>
                    </ph-list-row>
                    <ng-container *ngIf="q.allow_other">
                        <ng-container *ngTemplateOutlet="otherRow; context: { q: q }"></ng-container>
                    </ng-container>
                    <ph-hint *ngIf="multiSelectionCount(q) > 0">{{ multiSelectionCount(q) }} selected. Press Enter to confirm and continue.</ph-hint>
                </ph-stack>

                <ng-container *ngSwitchCase="'text'">
                    <ph-input #freeFormInput type="text" [placeholder]="q.prompt" aria-label="Your answer"
                              [value]="state[q.id]?.text ?? ''"
                              (input)="onTextInput(q, $any($event).composedPath()[0].value)"
                              (focus)="onInputFocus()" (blur)="onInputBlur()"></ph-input>
                </ng-container>

                <ng-container *ngSwitchCase="'textarea'">
                    <ph-textarea #freeFormInput rows="4" [placeholder]="q.prompt" aria-label="Your answer"
                                 [value]="state[q.id]?.text ?? ''"
                                 (input)="onTextInput(q, $any($event).composedPath()[0].value)"
                                 (focus)="onInputFocus()" (blur)="onInputBlur()"></ph-textarea>
                </ng-container>

                <ng-container *ngSwitchCase="'number'">
                    <ph-input #freeFormInput type="number" [placeholder]="q.prompt" aria-label="Your answer"
                              [value]="state[q.id]?.text ?? ''"
                              (input)="onTextInput(q, $any($event).composedPath()[0].value)"
                              (focus)="onInputFocus()" (blur)="onInputBlur()"></ph-input>
                </ng-container>

                <ng-container *ngSwitchCase="'date'">
                    <ph-input type="date" [placeholder]="q.prompt" aria-label="Your answer"
                              [value]="state[q.id]?.text ?? ''"
                              (input)="onTextInput(q, $any($event).composedPath()[0].value)"
                              (focus)="onInputFocus()" (blur)="onInputBlur()"></ph-input>
                </ng-container>

            <!-- UNIT PICKER (inline, same picker used by Custom Reports V2) -->
            <ng-container *ngSwitchCase="'unit_picker'">
                <div class="inline-unit-selector">
                    <app-unit-picker [columns]="unitColumns"
                                     [canShowDeleted]="false"
                                     [preselectedUnitIds]="pickedUnitIdsFor(q)"
                                     (unitsChange)="onUnitSelectionChanged(q, $event)"></app-unit-picker>
                </div>
                <ph-hint *ngIf="pickedUnitsFor(q).length > 0">
                    {{ pickedUnitsFor(q).length }} selected
                </ph-hint>
            </ng-container>

            <!-- PEOPLE PICKERS (member / owner / account) — the shared V2
                 people-picker; self-loads the org's memberships. Mirrors
                 the unit-picker wiring above (SD-8010 comment 55501). -->
            <ng-container *ngSwitchCase="'people_picker'">
                <div class="inline-people-picker">
                    <app-people-picker
                        [placeholder]="'Search people…'"
                        [preselectedPersonIds]="pickedPersonIdsFor(q)"
                        (selectionsChange)="onPeopleSelectionChanged(q, $event)">
                    </app-people-picker>
                </div>
                <ph-hint *ngIf="pickedPeopleFor(q).length > 0">
                    {{ pickedPeopleFor(q).length }} selected
                </ph-hint>
            </ng-container>

                <ng-container *ngSwitchCase="'picker_stub'">
                    <ph-alert status="information" variant="lighter" size="small">
                        The {{ pickerEntityLabel(q) }} picker isn't wired up yet. Skip this question and Gable will ask another way.
                    </ph-alert>
                </ng-container>
            </div>

            <ph-actions align="between">
                <ph-button type="button" variant="secondary" mode="filled" size="small" icon="arrow-left"
                           [disabled]="(currentIndex === 0 && !backOnFirstPage) || submitting"
                           (click)="prev()" aria-label="Previous question">Back</ph-button>
                <ph-button *ngIf="!isLastQuestion" type="button" size="small" trailing-icon="arrow-right"
                           [disabled]="!canAdvance || submitting"
                           (click)="next()" aria-label="Next question">Next</ph-button>
                <ph-button *ngIf="isLastQuestion" type="button" size="small"
                           [disabled]="!canAdvance || submitting"
                           (click)="next()" aria-label="Submit answers">Submit</ph-button>
            </ph-actions>
        </ng-container>
    </ph-stack>
</ng-template>

<ng-template #otherRow let-q="q">
    <ph-list-row *ngIf="!state[q.id]?.otherActive"
                 class="option-row other-row"
                 variant="card"
                 heading="Something else…"
                 [open]="keyboardActive && isOtherRowFocused(q)"
                 (ph-select)="onActivateOther(q)"
                 (mouseenter)="onRowHover(q.options?.length ?? 0)">
        <ph-kbd slot="actions">+</ph-kbd>
    </ph-list-row>
    <ph-input *ngIf="state[q.id]?.otherActive"
              #freeFormInput
              type="text"
              placeholder="Type your answer"
              aria-label="Your answer"
              [value]="state[q.id]?.otherText ?? ''"
              (input)="onOtherTextInput(q, $any($event).composedPath()[0].value)"
              (focus)="onInputFocus()" (blur)="onInputBlur()"></ph-input>
</ng-template>
